/**
 * useAlmostThere — shows a nudge pop-up EVERY time the subject page is opened
 * when ANY chapter is in progress (started but not finished).
 *
 * Shows remaining lectures / DPPs / tests per chapter and a badge reminder.
 * Empty or fully-upcoming chapters (nothing started) never trigger it.
 *
 * Usage in a subject page:
 *   const nudge = useAlmostThere('iitjee-physics', progress);
 *   ...
 *   {nudge.show && <AlmostThereModal {...nudge.props} onClose={nudge.dismiss} />}
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { inProgressChapters, subjectMeta } from '../utils/achievements';

export function useAlmostThere(subjectKey, progress) {
  const { currentUser } = useAuth();
  const [show, setShow] = useState(false);
  const [items, setItems] = useState([]);

  // progress here is the per-subject progress map: { [chapterId]: {...} }
  const fingerprint = useMemo(() => JSON.stringify(progress || {}), [progress]);

  useEffect(() => {
    if (!currentUser || !subjectKey) return;
    const wrapped = { [subjectKey]: progress || {} };
    const candidates = inProgressChapters(subjectKey, wrapped);
    if (candidates.length > 0) {
      setItems(candidates);
      setShow(true);
    } else {
      setShow(false);
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, subjectKey, currentUser?.uid]);

  const dismiss = useCallback(() => setShow(false), []);

  const meta = subjectMeta(subjectKey);
  return { show, dismiss, props: { items, subjectLabel: meta.label, hue: meta.hue, hue2: meta.hue2 } };
}
